import FunnelAPI from 'dashboard/api/funnels';

const state = {
  records: [],
  currentFunnel: null,
  cards: [],
  currentCard: null,
  activities: [],
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isFetchingCards: false,
    isFetchingCard: false,
    isMovingCard: false,
    isFetchingActivities: false,
  },
};

const getters = {
  getFunnels: $state => $state.records,
  getCurrentFunnel: $state => $state.currentFunnel,
  getCards: $state => $state.cards,
  getCurrentCard: $state => $state.currentCard,
  getActivities: $state => $state.activities,
  getCardsByStage: $state => stageId =>
    $state.cards
      .filter(c => c.funnel_stage_id === stageId)
      .sort((a, b) => a.position - b.position),
  getUIFlags: $state => $state.uiFlags,
};

const actions = {
  async get({ commit }) {
    commit('setUIFlag', { isFetching: true });
    try {
      const { data } = await FunnelAPI.get();
      commit('setFunnels', data);
    } finally {
      commit('setUIFlag', { isFetching: false });
    }
  },

  async show({ commit }, id) {
    commit('setUIFlag', { isFetching: true });
    try {
      const { data } = await FunnelAPI.show(id);
      commit('setCurrentFunnel', data);
      return data;
    } finally {
      commit('setUIFlag', { isFetching: false });
    }
  },

  async create({ commit }, { funnel, shared_with }) {
    commit('setUIFlag', { isCreating: true });
    try {
      const { data } = await FunnelAPI.create({ funnel, shared_with });
      commit('addFunnel', data);
      return data;
    } finally {
      commit('setUIFlag', { isCreating: false });
    }
  },

  async update({ commit }, { id, funnel, shared_with }) {
    commit('setUIFlag', { isUpdating: true });
    try {
      const { data } = await FunnelAPI.update(id, { funnel, shared_with });
      commit('updateFunnel', data);
      return data;
    } finally {
      commit('setUIFlag', { isUpdating: false });
    }
  },

  async delete({ commit }, id) {
    commit('setUIFlag', { isDeleting: true });
    try {
      await FunnelAPI.delete(id);
      commit('removeFunnel', id);
    } finally {
      commit('setUIFlag', { isDeleting: false });
    }
  },

  async getCards({ commit }, funnelId) {
    commit('setUIFlag', { isFetchingCards: true });
    try {
      const { data } = await FunnelAPI.getCards(funnelId);
      commit('setCards', data);
    } finally {
      commit('setUIFlag', { isFetchingCards: false });
    }
  },

  async showCard({ commit }, { funnelId, cardId }) {
    commit('setUIFlag', { isFetchingCard: true });
    try {
      const { data } = await FunnelAPI.showCard(funnelId, cardId);
      commit('setCurrentCard', data);
      return data;
    } finally {
      commit('setUIFlag', { isFetchingCard: false });
    }
  },

  async createCard({ commit }, { funnelId, ...cardData }) {
    const { data } = await FunnelAPI.createCard(funnelId, cardData);
    commit('addCard', data);
    return data;
  },

  async updateCard({ commit }, { funnelId, cardId, ...cardData }) {
    const { data } = await FunnelAPI.updateCard(funnelId, cardId, cardData);
    commit('updateCard', data);
    commit('setCurrentCard', data);
    return data;
  },

  async deleteCard({ commit }, { funnelId, cardId }) {
    await FunnelAPI.deleteCard(funnelId, cardId);
    commit('removeCard', cardId);
  },

  async moveCard(
    { commit, state: $state },
    { funnelId, cardId, funnelStageId, position }
  ) {
    commit('setUIFlag', { isMovingCard: true });
    try {
      const { data } = await FunnelAPI.moveCard(funnelId, cardId, {
        funnel_stage_id: funnelStageId,
        position,
      });
      commit('updateCard', data);
      if ($state.currentCard?.id === data.id) {
        commit('setCurrentCard', data);
      }
      return data;
    } finally {
      commit('setUIFlag', { isMovingCard: false });
    }
  },

  async markCardWon({ commit }, { funnelId, cardId }) {
    const { data } = await FunnelAPI.markCardWon(funnelId, cardId);
    commit('updateCard', data);
    commit('setCurrentCard', data);
    return data;
  },

  async markCardLost({ commit }, { funnelId, cardId, lossReason }) {
    const { data } = await FunnelAPI.markCardLost(funnelId, cardId, lossReason);
    commit('updateCard', data);
    commit('setCurrentCard', data);
    return data;
  },

  async attachMessageToCard(_, { funnelId, cardId, messageId, documentName }) {
    const { data } = await FunnelAPI.attachMessageAttachments(
      funnelId,
      cardId,
      messageId,
      documentName
    );
    return data;
  },

  async getActivities({ commit }, { funnelId, cardId }) {
    commit('setUIFlag', { isFetchingActivities: true });
    try {
      const { data } = await FunnelAPI.getActivities(funnelId, cardId);
      commit('setActivities', data);
    } finally {
      commit('setUIFlag', { isFetchingActivities: false });
    }
  },

  async createActivity(
    { commit },
    { funnelId, cardId, formData, ...activityData }
  ) {
    const { data } = await FunnelAPI.createActivity(
      funnelId,
      cardId,
      formData || activityData
    );
    commit('addActivity', data);
    return data;
  },

  async updateActivity(
    { commit },
    { funnelId, cardId, activityId, ...activityData }
  ) {
    const { data } = await FunnelAPI.updateActivity(
      funnelId,
      cardId,
      activityId,
      activityData
    );
    commit('updateActivity', data);
    return data;
  },

  async deleteActivity({ commit }, { funnelId, cardId, activityId }) {
    await FunnelAPI.deleteActivity(funnelId, cardId, activityId);
    commit('removeActivity', activityId);
  },
};

const mutations = {
  setUIFlag($state, flag) {
    $state.uiFlags = { ...$state.uiFlags, ...flag };
  },
  setFunnels($state, funnels) {
    $state.records = funnels;
  },
  setCurrentFunnel($state, funnel) {
    $state.currentFunnel = funnel;
  },
  addFunnel($state, funnel) {
    $state.records.push(funnel);
  },
  updateFunnel($state, funnel) {
    const idx = $state.records.findIndex(f => f.id === funnel.id);
    if (idx !== -1) $state.records.splice(idx, 1, funnel);
    if ($state.currentFunnel?.id === funnel.id) {
      $state.currentFunnel = funnel;
    }
  },
  removeFunnel($state, id) {
    $state.records = $state.records.filter(f => f.id !== id);
    if ($state.currentFunnel?.id === id) {
      $state.currentFunnel = null;
    }
  },
  setCards($state, cards) {
    $state.cards = cards;
  },
  setCurrentCard($state, card) {
    $state.currentCard = card;
  },
  addCard($state, card) {
    $state.cards.push(card);
  },
  updateCard($state, card) {
    const idx = $state.cards.findIndex(c => c.id === card.id);
    if (idx !== -1) $state.cards.splice(idx, 1, card);
    else $state.cards.push(card);
  },
  removeCard($state, cardId) {
    $state.cards = $state.cards.filter(c => c.id !== cardId);
  },
  setActivities($state, activities) {
    $state.activities = activities;
  },
  addActivity($state, activity) {
    $state.activities.unshift(activity);
  },
  updateActivity($state, activity) {
    const idx = $state.activities.findIndex(a => a.id === activity.id);
    if (idx !== -1) $state.activities.splice(idx, 1, activity);
  },
  removeActivity($state, activityId) {
    $state.activities = $state.activities.filter(a => a.id !== activityId);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
